import { useMemo } from "react";
import { SwiperSlide } from "swiper/react";
import { BaseCarousel } from "../BaseCarousel";
import { ProductCarouselSlide } from "./ProductCarouselSlide";
import type { CarouselProduct } from "@shared/types/carousel";
import "./ProductCarousel.css";

interface ProductCarouselProps {
  title?: string;
  subtitle?: string;
  products: CarouselProduct[];
  className?: string;
}

export function ProductCarousel({
  title,
  subtitle,
  products,
  className = "",
}: ProductCarouselProps) {
  if (!products || products.length === 0) {
    return null;
  }

  const instanceClassName = useMemo(
    () => `product-carousel-${Math.random().toString(36).slice(2, 8)}`,
    []
  );

  const navigationConfig = useMemo(
    () => ({
      prevEl: `.${instanceClassName}__nav-prev`,
      nextEl: `.${instanceClassName}__nav-next`,
    }),
    [instanceClassName]
  );

  return (
    <section className={`product-carousel-section ${className}`}>
      {(title || subtitle) && (
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-2">
          <div>
            {title && (
              <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-gray-600 text-base md:text-lg">{subtitle}</p>
            )}
          </div>
        </div>
      )}

      <div className="relative">
        <BaseCarousel
          className={`${instanceClassName}`}
          slidesPerView={1.0}
          spaceBetween={16}
          breakpoints={{
            640: { slidesPerView: 2.0, spaceBetween: 20 },
            768: { slidesPerView: 3.0, spaceBetween: 24 },
            1024: { slidesPerView: 4.0, spaceBetween: 28 },
            1280: { slidesPerView: 5.0, spaceBetween: 32 },
          }}
          navigation={navigationConfig}
          pagination={{ clickable: true }}
          autoplay={false}
          loop={false}
        >
          {products.map((product, index) => (
            <SwiperSlide key={product._id || index} className="!h-auto">
              <ProductCarouselSlide product={product} />
            </SwiperSlide>
          ))}
        </BaseCarousel>
        <button
          type="button"
          aria-label="Anterior"
          className={`${instanceClassName}__nav-prev absolute left-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white/90 p-1 text-gray-600 shadow transition hover:bg-white hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#282828] sm:p-2`}
        >
          <svg
            className="h-4 w-4 sm:h-5 sm:w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          type="button"
          aria-label="Siguiente"
          className={`${instanceClassName}__nav-next absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white/90 p-1 text-gray-600 shadow transition hover:bg-white hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#282828] sm:p-2`}
        >
          <svg
            className="h-4 w-4 sm:h-5 sm:w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </section>
  );
}
